import React from "react";
import {Box, Button, Flex, HStack, Icon, Spinner, Text} from "@chakra-ui/react";
import {FiAlertCircle, FiPaperclip} from "react-icons/fi";

import {OUTBOX_STATUS} from "./outbox";

/**
 * The patient's messages the server has not stored yet, at the end of their
 * thread (outbox.js; blhWebServer ADR 0018 §5). Each says where it is:
 * "Sending…" until the server has it, then it is the thread's own message;
 * "Not sent" with the reason, Retry (the same message again) and Remove. A
 * message not sent stays, its text readable, until the patient chooses.
 */
export function PendingMessages({entries, onRetry, onRemove}) {
  if (!entries || entries.length === 0) return null;
  return (
    <Box as="ul" listStyleType="none" aria-label="Messages being sent" mt={2}>
      {entries.map((entry) => (
        <PendingMessage key={entry.clientMessageId} entry={entry} onRetry={onRetry} onRemove={onRemove} />
      ))}
    </Box>
  );
}

function PendingMessage({entry, onRetry, onRemove}) {
  const failed = entry.status === OUTBOX_STATUS.FAILED;
  const isFile = entry.messageType === "file";
  return (
    <Flex as="li" direction="column" align="flex-end" py={1} data-client-message-id={entry.clientMessageId}>
      <Box
        maxW="70%"
        px={3}
        py={2}
        rounded="lg"
        bg={failed ? "red.50" : "#f0f0ff"}
        color={failed ? "gray.800" : "gray.600"}
        fontSize="14px"
        whiteSpace="pre-wrap"
        wordBreak="break-word"
      >
        {isFile ? (
          <HStack spacing={1.5}>
            <Icon as={FiPaperclip} aria-hidden="true" />
            <Text as="span">{entry.attachment?.originalName || entry.message}</Text>
          </HStack>
        ) : (
          entry.message
        )}
      </Box>
      {failed ? (
        <HStack spacing={2} mt={1} fontSize="12px" role="alert">
          <HStack spacing={1} color="red.600">
            <Icon as={FiAlertCircle} aria-hidden="true" />
            <Text as="span">{entry.error?.sentence || "Not sent."}</Text>
          </HStack>
          {entry.error?.retryable !== false && (
            <Button variant="link" size="xs" colorScheme="purple" onClick={() => onRetry?.(entry.clientMessageId)}>
              Retry
            </Button>
          )}
          <Button variant="link" size="xs" color="gray.600" onClick={() => onRemove?.(entry.clientMessageId)}>
            Remove
          </Button>
        </HStack>
      ) : (
        <HStack spacing={1} mt={1} fontSize="12px" color="gray.400" aria-live="polite">
          <Spinner size="xs" aria-hidden="true" />
          <Text as="span">Sending…</Text>
        </HStack>
      )}
    </Flex>
  );
}
